"use client";

import React from "react";
import { motion } from "framer-motion";
import { Lock, Mail, ArrowRight, ShieldCheck } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
function LoginForm() {
  const {
    isSubmitting,
    handleSubmit,
    control
  } = useWvcForm();
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="email" rules={{
      required: "Email address is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default">Email Address</FormLabel>
            <FormControl>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                <Input type="email" placeholder="you@example.com" className="pl-10 h-12 transition-colors duration-300 focus-visible:ring-2 focus-visible:ring-accent" {...field} />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="password" rules={{
      required: "Password is required",
      minLength: {
        value: 6,
        message: "Password must be at least 6 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <div className="flex items-center justify-between">
              <FormLabel className="font-default">Password</FormLabel>
              <Link to="#" className="text-sm font-medium text-primary hover:text-accent transition-colors duration-300">
                Forgot Password?
              </Link>
            </div>
            <FormControl>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                <Input type="password" placeholder="••••••••" className="pl-10 pr-10 h-12 transition-colors duration-300 focus-visible:ring-2 focus-visible:ring-accent" {...field} />
                <ShieldCheck className="absolute right-3 top-1/2 -translate-y-1/2 size-4 text-accent opacity-70" />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="remember" render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 py-2">
            <FormControl>
              <Checkbox checked={field.value} onCheckedChange={field.onChange} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="font-normal text-muted-foreground cursor-pointer">
                Remember me for 30 days
              </FormLabel>
            </div>
          </FormItem>} />

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 group transition-colors duration-300 hover:bg-accent hover:text-accent-foreground focus:ring-2 focus:ring-accent hover:scale-[1.02] hover:shadow-md">
        {isSubmitting ? "Signing in..." : <>
            Sign In
            <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
          </>}
      </Button>
    </Form>;
}
export default function UserLogin() {
  return <section id="userlogin" className="min-h-screen flex items-center justify-center bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-background via-secondary/30 to-accent/5 py-12 px-4 sm:px-6 lg:px-8">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6,
      ease: "easeOut"
    }} className="w-full max-w-md">
        <Card className="shadow-xl border-2 border-accent/20 backdrop-blur-sm bg-card/95 rounded-lg overflow-hidden">
          <CardHeader className="space-y-2 text-center pb-8">
            <div className="mx-auto bg-accent/10 p-3 rounded-lg w-fit mb-2">
              <Lock className="size-6 text-accent" />
            </div>
            <CardTitle className="text-2xl font-default tracking-tight">
              Welcome Back
            </CardTitle>
            <CardDescription className="text-base">
              Sign in to your Lush Fashion account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <FormProvider formId="user-login-form" sectionName="UserLogin" defaultValues={{
            email: "",
            password: "",
            remember: false
          }} submitText="Sign In" successMessage="Successfully logged in!">
              <LoginForm />
            </FormProvider>
          </CardContent>
          <CardFooter className="flex flex-col items-center justify-center border-t border-border/50 pt-6 mt-2">
            <p className="text-sm text-muted-foreground">
              Don't have an account?{" "}
              <Link to="#" className="font-semibold text-primary hover:text-accent transition-colors duration-300">
                Create Account
              </Link>
            </p>
          </CardFooter>
        </Card>
      </motion.div>
    </section>;
}